import _ from "lodash";
import { useEffect, useMemo, useRef } from "react";

import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import { ListItemIcon, ListItemText } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { ContactsAnalyticsService } from "@analytics/index";

import ContactsTable from "@components/ContactTable/ContactsTable";
import useContactsTableConfig from "@components/ContactTable/useContactsTableConfig";
import useContactsTableToolbar from "@components/ContactTable/useContactsTableToolbar";
import ContactFilterDrawer from "@components/FilterControls/ContactFilterDrawer";
import {
  TableToolbar,
  TableToolbarDropdown,
  TableToolbarDropdownMenuItem,
  TableToolbarFilterButton,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";

import C from "@constants/constants";

import { useGetContactsQuery } from "@queries/contact.query";

import { useAdminStore } from "@store/adminStore";
import { IContactTableControls } from "@store/filters/filter.types";
import { useTableStore } from "@store/filters/filters.store";
import { useUserStore } from "@store/userStore";

export default function CompaniesContacts() {
  const { isAdminDrawerOpen, setIsAdminDrawerOpen } = useAdminStore();

  const { role } = useUserStore();

  const { filters, pagination, resetTableStore, selection, setTableFilters, setTableSelection, sort } =
    useTableStore("contact");

  const oldFilters = useRef<IContactTableControls>({
    filters,
    pagination,
    selection,
    sort,
  });

  const {
    data: contactResults = {
      contacts: [],
      currentPage: 0,
      total: 0,
      totalPages: 0,
    },
    isPlaceholderData,
    isRefetching,
    status: loadingContactsStatus,
  } = useGetContactsQuery();

  useEffect(() => {
    ContactsAnalyticsService.trackContactFilter(
      { filters, pagination, selection, sort },
      oldFilters.current,
      "smart-search",
    );
    oldFilters.current = {
      filters,
      pagination,
      selection,
      sort,
    };
  }, [filters, pagination, sort, selection]);

  useEffect(() => {
    return () => {
      resetTableStore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredContacts = _.sortBy(contactResults.contacts, "organization.name");

  const { handleAddContactsToTechnology, handleExportContacts, isFilterOpen, toggleFilters } = useContactsTableToolbar({
    contacts: filteredContacts,
    selectedIds: selection ?? [],
    workflow: "smart-search",
  });

  const { columns } = useContactsTableConfig({
    workflow: "smart-search",
  });

  const activeFilters = useMemo(() => {
    return Object.entries(filters).reduce((count, [_, value]) => {
      if (Array.isArray(value)) {
        return count + value.length;
      }
      return count + (value ? 1 : 0);
    }, 0);
  }, [filters]);

  return (
    <>
      <ContactFilterDrawer
        filters={filters}
        isOpen={isFilterOpen}
        resetFilters={() => resetTableStore({ filters: true })}
        setFilters={setTableFilters}
        setOpen={toggleFilters}
      />
      <TableToolbar>
        <TableToolbarLeft>
          <TableToolbarTitle
            resourceLink={C.LOOM_CONTACTS}
            tableSubtitle="Save contacts to your Portfolio or download to CSV. All contact emails will appear in your CSV."
            tableTitle="Contacts"
          />
        </TableToolbarLeft>
        <TableToolbarRight>
          <TableToolbarFilterButton
            activeFilters={activeFilters}
            toggleFilterVisibility={toggleFilters}
            tooltipText="Filter contacts from company."
          />
          <TableToolbarDropdown
            actionsLabel="Save Contacts"
            selectedIds={selection ?? []}
            tooltipText="Save selected contacts to Portfolio or download to CSV."
          >
            <TableToolbarDropdownMenuItem onClick={handleAddContactsToTechnology}>
              <ListItemIcon>
                <LightbulbRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Save To Technology</ListItemText>
            </TableToolbarDropdownMenuItem>
            <TableToolbarDropdownMenuItem onClick={handleExportContacts}>
              <ListItemIcon>
                <DownloadRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export Contact Data</ListItemText>
            </TableToolbarDropdownMenuItem>
          </TableToolbarDropdown>
          {role === "Admin" && (
            <Button
              color={isAdminDrawerOpen ? "primary" : "secondary"}
              onClick={() => setIsAdminDrawerOpen(true)}
              startIcon={<AdminPanelSettingsRoundedIcon />}
              sx={{ borderRadius: "8px", height: "40px" }}
              variant="contained"
            >
              <Typography fontWeight={600} variant="body2">
                Admin Drawer
              </Typography>
            </Button>
          )}
        </TableToolbarRight>
      </TableToolbar>
      <ContactsTable
        columns={columns}
        contacts={filteredContacts}
        isLoading={loadingContactsStatus === "pending" || isRefetching || isPlaceholderData}
        paginationMode="server"
        selectedIds={selection}
        setSelectedIds={setTableSelection}
        total={contactResults.total}
      />
    </>
  );
}
